import React, { useState } from 'react';
import {
  Box, Drawer, AppBar, Toolbar, Typography, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, IconButton, Avatar,
  Menu, MenuItem, Divider, Chip, useMediaQuery, useTheme, Tooltip,
  Container, Snackbar, Alert,
} from '@mui/material';
import {
  Dashboard, People, DirectionsCarFilled, VerifiedUser,
  BadgeOutlined, EvStation, ReportProblem, ConfirmationNumber,
  Notifications, Menu as MenuIcon, Logout, Lock, Settings,
  ElectricScooter, BarChart, Close, Navigation, AnalyticsOutlined,
  Assessment, TrendingUp, BatteryChargingFull,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ROLES } from '../config/Constants';
import logo from '../assets/logo.png';

const DRAWER_W = 256;

// ── Nav definition ── role arrays = who can see it (empty = all roles)
const NAV = [
  { label: 'Dashboard',        icon: <Dashboard />,           path: '/dashboard',  roles: [] },
  { label: 'Users',            icon: <People />,              path: '/users',      roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER] },
  { label: 'Vehicles',         icon: <ElectricScooter />,     path: '/vehicles',   roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER] },
  { label: 'KYC Management',   icon: <VerifiedUser />,        path: '/kyc',        roles: [ROLES.SUPERADMIN, ROLES.ADMIN] },
  { label: 'Employee Manager', icon: <BadgeOutlined />,       path: '/employees',  roles: [ROLES.SUPERADMIN, ROLES.ADMIN] },
  { label: 'Battery Stations', icon: <EvStation />,           path: '/stations',   roles: [] },
  { label: 'Complaints',       icon: <ReportProblem />,       path: '/complaints', roles: [] },
  { label: 'Tickets',          icon: <ConfirmationNumber />,  path: '/tickets',    roles: [] },
  { label: 'Notifications',    icon: <Notifications />,       path: '/notifications', roles: [] },
  { label: 'Vehicle Assignments', icon: <Assessment />,       path: '/report/vehicle-assignments', roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER] },
  { label: 'Fleet Report',     icon: <TrendingUp />,          path: '/report/fleet-movement', roles: [ROLES.SUPERADMIN, ROLES.ADMIN, ROLES.MANAGER] },
  { label: 'Reports',          icon: <BarChart />,            path: '/reports',    roles: [ROLES.SUPERADMIN, ROLES.ADMIN] },
  { label: 'Settings',         icon: <Settings />,            path: '/settings',   roles: [ROLES.SUPERADMIN] },
];

const SECTION_BREAKS = new Set(['/stations', '/report/vehicle-assignments', '/reports']);

// ── Error Boundary ──────────────────────────────────────────────────────────
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Page crashed:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            Something went wrong
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {this.state.error?.message || 'An unexpected error occurred.'}
          </Typography>
          <button
            onClick={() => this.setState({ hasError: false })}
            style={{
              padding: '8px 16px',
              background: '#0A2540',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Try Again
          </button>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl,   setAnchorEl]   = useState(null);
  const [snack, setSnack]           = useState({ open: false, msg: '', sev: 'info' });
  const { user, logout }            = useAuth();
  const navigate                    = useNavigate();
  const location                    = useLocation();
  const theme                       = useTheme();
  const isMobile                    = useMediaQuery(theme.breakpoints.down('md'));

  const role         = user?.role?.toLowerCase() ?? '';
  const visibleNav   = NAV.filter(n => n.roles.length === 0 || n.roles.includes(role));
  const currentLabel = NAV.find(n => location.pathname.startsWith(n.path))?.label ?? 'Ridexcity';

  const handleLogout = () => {
    logout();
    setSnack({ open: true, msg: 'Logged out successfully.', sev: 'success' });
    setTimeout(() => navigate('/login'), 1000);
  };

  const sidebarContent = (
    <Box sx={{
      height: '100%', display: 'flex', flexDirection: 'column',
      background: 'linear-gradient(180deg, #1a237e 0%, #0d47a1 100%)',
      overflowY: 'auto',
    }}>
      {/* Brand */}
      <Box sx={{ p: 2.5, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
        <Box sx={{
          width: 70, height: 42, borderRadius: 1, background: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          p: 0, flexShrink: 0,
        }}>
          <img src={logo} alt="Ridexcity" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: 2 }} />
        </Box>
        <Box>
          <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>
            Ridexcity
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 11 }}>
            Admin Panel
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mx: 2 }} />

      {/* Role chip */}
      <Box sx={{ px: 2, py: 1 }}>
        <Chip
          label={user?.role ?? 'Admin'}
          size="small"
          sx={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontWeight: 600, fontSize: 11 }}
        />
      </Box>

      {/* Nav items */}
      <List sx={{ flex: 1, px: 1.5, pt: 0 }}>
        {visibleNav.map((item, idx) => {
          const active = location.pathname.startsWith(item.path);
          const showDivider = idx > 0 && SECTION_BREAKS.has(item.path);
          return (
            <React.Fragment key={item.path}>
              {showDivider && <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.8, mx: 1 }} />}
              <ListItem disablePadding sx={{ mb: 0.3 }}>
                <ListItemButton
                  onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
                  sx={{
                    borderRadius: 1, py: 1,
                    background: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                    color: active ? '#00C6FF' : '#b0bec5',
                    transition: 'all 0.3s ease',
                    '&:hover': { background: 'rgba(255,255,255,0.08)', color: '#fff' },
                  }}
                  aria-current={active ? 'page' : undefined}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 500 }} />
                  {active && <Box sx={{ width: 3, height: 20, background: '#00C6FF', borderRadius: 999 }} />}
                </ListItemButton>
              </ListItem>
            </React.Fragment>
          );
        })}
      </List>

      {/* User info footer */}
      <Box sx={{ px: 2, py: 2, borderTop: '1px solid rgba(255,255,255,0.1)', flexShrink: 0 }}>
        <Tooltip title={user?.name || 'Admin'}>
          <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {user?.name || 'Admin User'}
          </Typography>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden', background: '#F4F7FB' }}>
      {/* Desktop Sidebar — fixed height, its own scroll */}
      <Box
        sx={{
          width: DRAWER_W,
          flexShrink: 0,
          height: '100vh',
          overflow: 'hidden',
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
        }}
      >
        {sidebarContent}
      </Box>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { width: DRAWER_W },
        }}
      >
        {sidebarContent}
      </Drawer>

      {/* Main content — fills remaining width, scrolls independently */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', minWidth: 0 }}>
        {/* Top AppBar — sticky, never scrolls away */}
        <AppBar
          position="static"
          sx={{
            flexShrink: 0,
            background: '#fff',
            color: '#0A2540',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            borderBottom: '1px solid #E2E8F0',
          }}
        >
          <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={() => setMobileOpen(!mobileOpen)}
                sx={{ display: { xs: 'flex', md: 'none' }, color: '#0A2540' }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" sx={{ fontWeight: 700, display: { xs: 'none', sm: 'block' }, color: '#1a237e' }}>
                {currentLabel}
              </Typography>
            </Box>

            {/* User menu */}
            <IconButton
              onClick={(e) => setAnchorEl(e.currentTarget)}
              sx={{ width: 40, height: 40 }}
            >
              <Avatar sx={{ width: 36, height: 36, background: '#1a237e', fontSize: 14, fontWeight: 700 }}>
                {(user?.name ?? 'A').charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {user?.name || 'Admin'}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => navigate('/settings')} sx={{ fontSize: 14 }}>
                <Lock sx={{ mr: 1, fontSize: 18 }} /> Settings
              </MenuItem>
              <MenuItem onClick={handleLogout} sx={{ fontSize: 14, color: '#ef4444' }}>
                <Logout sx={{ mr: 1, fontSize: 18 }} /> Logout
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* Page content — ONLY this area scrolls */}
        <Box sx={{ flex: 1, overflow: 'auto', p: { xs: 2, sm: 3, md: 3 } }}>
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnack({ ...snack, open: false })}
          severity={snack.sev}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
